"use client";
/**
 * Compact viewport — phone / tablet / desktop classification for APP CHROME.
 *
 * Pane content still follows principle 18 (container queries, never `sm:` /
 * `lg:` inside a pane). The shell owns the WINDOW: a 390-px iPhone cannot
 * show twenty top-nav tabs, and a 1×6 ultrawide tree cannot stay 1×6. This
 * module is the one place those window decisions live.
 *
 * Widths are CSS pixels (Safari's `innerWidth` after the viewport meta).
 * iPhone portrait lands in `phone`; iPad portrait and most iPad landscape
 * land in `tablet`; a 1280+ laptop / iPad Pro 12.9 landscape is `desktop`.
 *
 * The hook snapshots a *bucket* via matchMedia, not raw `innerWidth`. iOS
 * fires `resize` when the URL bar shows or hides (and sometimes reports a
 * 1-px width twitch). Subscribing to every resize remounted every chart in
 * a restacked tree and killed the WebKit tab.
 *
 * Runtime reflow (stackGridForCompact / reflowForLaptop(cols)) is a DISPLAY
 * transform. It must not be written back to the stored layout — a phone
 * session must not restack the ultrawide profile.
 */
import { useLayoutEffect, useSyncExternalStore } from "react";

/** Inclusive upper bound for the phone chrome (iPhone 16 Pro Max portrait is 440). */
export const PHONE_MAX_PX = 767;
/** Inclusive upper bound for tablet chrome (iPad Pro 12.9 portrait is 1024). */
export const TABLET_MAX_PX = 1279;
/** Wide-tablet floor — 3-col tile strips only. iPad landscape / iPad Pro portrait. */
export const WIDE_TABLET_MIN_PX = 1024;

export type CompactMode = "phone" | "tablet" | "desktop";

/** Stable matchMedia bucket. A string so useSyncExternalStore can Object.is it. */
export type CompactSnap = "phone" | "tablet" | "tablet-wide" | "desktop";

export type CompactViewport = {
  /** Representative CSS px for the current bucket — not a live innerWidth. */
  width: number;
  mode: CompactMode;
  compact: boolean;
  phone: boolean;
  tablet: boolean;
  wideTablet: boolean;
};

/** Primary destinations on the iPhone tab bar, in order. Missing / hidden
 *  nav items are skipped at render time. "More" is always appended. */
export const COMPACT_TAB_HREFS = ["/flow", "/dashboard", "/scanner", "/charts"] as const;

export function classifyCompactWidth(width: number): CompactMode {
  return modeFromSnap(compactSnapFromWidth(width));
}

export function compactSnapFromWidth(width: number): CompactSnap {
  if (!Number.isFinite(width) || width <= 0) return "desktop";
  if (width <= PHONE_MAX_PX) return "phone";
  if (width <= TABLET_MAX_PX) return width >= WIDE_TABLET_MIN_PX ? "tablet-wide" : "tablet";
  return "desktop";
}

export function modeFromSnap(snap: CompactSnap): CompactMode {
  if (snap === "phone") return "phone";
  if (snap === "tablet" || snap === "tablet-wide") return "tablet";
  return "desktop";
}

/** Representative width so `suggestedCompactCols(mode, width)` stays correct
 *  without reading a jittering innerWidth. */
export function widthForSnap(snap: CompactSnap): number {
  if (snap === "phone") return 390;
  if (snap === "tablet") return 820;
  if (snap === "tablet-wide") return 1100;
  return TABLET_MAX_PX + 1;
}

/** How many workspace columns a compact canvas can carry without postage stamps. */
export function suggestedCompactCols(mode: CompactMode, width: number, tileStrip = false): 1 | 2 | 3 {
  if (mode === "phone") return 1;
  if (mode === "tablet") return width >= WIDE_TABLET_MIN_PX && tileStrip ? 3 : 2;
  return tileStrip ? 3 : 2;
}

export type CompactGridItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
};

/** Floor so a stacked phone pane is a usable chart, not a title bar. */
export const PHONE_MIN_ROWS = 16;

function finiteHeight(h: unknown): number {
  return typeof h === "number" && Number.isFinite(h) && h > 0 ? h : 0;
}

/**
 * Restack an RGL `lg` list for a compact canvas. Order is reading order
 * (y then x). Leaf identities are preserved; width is rewritten to fill
 * `cols` tracks of a 12-col grid. Phone (1 col) floors height so a chart
 * is tappable. Tablet keeps stored heights — bumping every pane to
 * PHONE_MIN_ROWS on a 2-col iPad is what made the page taller than the
 * tab could hold. Idempotent on an already-stacked list. Fewer items than
 * tracks share the whole width between them.
 */
export function stackGridForCompact(items: CompactGridItem[], cols: 1 | 2 | 3): CompactGridItem[] {
  if (items.length === 0) return items;
  // Never more tracks than items: a page with one panel (every Wealth tab) on
  // a two-track tablet was drawn at half the window with the other half empty.
  const n = Math.max(1, Math.min(3, cols, items.length)) as 1 | 2 | 3;
  const sorted = [...items].sort((a, b) => a.y - b.y || a.x - b.x);
  const colW = 12 / n;
  let y = 0;
  let rowH = 0;
  return sorted.map((it, i) => {
    const col = i % n;
    if (col === 0 && i > 0) {
      y += rowH;
      rowH = 0;
    }
    const rawH = finiteHeight(it.h);
    const h = n === 1 ? Math.max(rawH, PHONE_MIN_ROWS) : rawH || PHONE_MIN_ROWS;
    rowH = Math.max(rowH, h);
    return { ...it, x: col * colW, y, w: colW, h };
  });
}

const CLASS_PHONE = "mpp-phone";
const CLASS_TABLET = "mpp-tablet";
const CLASS_COMPACT = "mpp-compact";

export function applyCompactClasses(mode: CompactMode, root: HTMLElement = document.documentElement): void {
  root.classList.toggle(CLASS_PHONE, mode === "phone");
  root.classList.toggle(CLASS_TABLET, mode === "tablet");
  root.classList.toggle(CLASS_COMPACT, mode !== "desktop");
}

/** Boot snippet — same classification as classifyCompactWidth, inlined so
 *  first paint does not flash the desktop nav on an iPhone. Kept next to
 *  the TS so a width-bound change cannot drift. */
export const COMPACT_BOOT_SCRIPT = `(function(){try{var w=window.innerWidth;var r=document.documentElement;r.classList.remove("${CLASS_PHONE}","${CLASS_TABLET}","${CLASS_COMPACT}");if(w<=${PHONE_MAX_PX}){r.classList.add("${CLASS_PHONE}","${CLASS_COMPACT}");}else if(w<=${TABLET_MAX_PX}){r.classList.add("${CLASS_TABLET}","${CLASS_COMPACT}");}}catch(e){}})();`;

const MQ_PHONE = `(max-width: ${PHONE_MAX_PX}px)`;
const MQ_TABLET = `(max-width: ${TABLET_MAX_PX}px)`;
const MQ_WIDE = `(min-width: ${WIDE_TABLET_MIN_PX}px)`;

function readSnap(): CompactSnap {
  if (window.matchMedia(MQ_PHONE).matches) return "phone";
  if (window.matchMedia(MQ_TABLET).matches) {
    return window.matchMedia(MQ_WIDE).matches ? "tablet-wide" : "tablet";
  }
  return "desktop";
}

function subscribeSnap(cb: () => void): () => void {
  const q = [window.matchMedia(MQ_PHONE), window.matchMedia(MQ_TABLET), window.matchMedia(MQ_WIDE)];
  for (const mq of q) mq.addEventListener("change", cb);
  return () => {
    for (const mq of q) mq.removeEventListener("change", cb);
  };
}

function serverSnap(): CompactSnap {
  return "desktop";
}

export function useCompactViewport(): CompactViewport {
  const snap = useSyncExternalStore(subscribeSnap, readSnap, serverSnap);
  const mode = modeFromSnap(snap);
  useLayoutEffect(() => {
    applyCompactClasses(mode);
  }, [mode]);
  return {
    width: widthForSnap(snap),
    mode,
    compact: mode !== "desktop",
    phone: mode === "phone",
    tablet: mode === "tablet",
    wideTablet: snap === "tablet-wide",
  };
}
